/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Payments Hooks - React Query for Payments
 * ============================================================================
 *
 * Custom hooks for payment data fetching and mutations using React Query.
 * Includes payment initiation, status checking, and payment history.
 *
 * ============================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  paymentsService,
  PaymentStatusResult,
  PollingStatus,
} from '@/services/superadmin/payments.service';
import { Payment, PaymentFilterParams, InitiatePaymentResponse } from '@/types/superadmin.types';
import { ApiError, PaginatedResponse, PaginationParams } from '@/services/api/types';
import { invoiceKeys } from './useInvoices';
import { subscriptionKeys } from './useSubscriptions';

// Query keys for cache management
export const paymentKeys = {
  all: ['payments'] as const,
  lists: () => [...paymentKeys.all, 'list'] as const,
  list: (params?: PaginationParams & PaymentFilterParams) => [...paymentKeys.lists(), params] as const,
  orgList: (orgId: string) => [...paymentKeys.all, 'org', orgId] as const,
  details: () => [...paymentKeys.all, 'detail'] as const,
  detail: (id: string) => [...paymentKeys.details(), id] as const,
  byInvoice: (invoiceNumber: string) => [...paymentKeys.all, 'invoice', invoiceNumber] as const,
  stats: () => [...paymentKeys.all, 'stats'] as const,
  status: (gateway: string, transactionId: string) =>
    [...paymentKeys.all, 'status', gateway, transactionId] as const,
  pollingStatus: () => [...paymentKeys.all, 'polling'] as const,
};

/**
 * Hook to fetch paginated payments list
 */
export function usePayments(params?: PaginationParams & PaymentFilterParams) {
  return useQuery<PaginatedResponse<Payment>, ApiError>({
    queryKey: paymentKeys.list(params),
    queryFn: () => paymentsService.getPayments(params),
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to fetch single payment detail
 */
export function usePayment(id: string) {
  return useQuery<Payment, ApiError>({
    queryKey: paymentKeys.detail(id),
    queryFn: () => paymentsService.getPayment(id),
    enabled: !!id,
  });
}

/**
 * Hook to fetch payment statistics
 */
export function usePaymentStats() {
  return useQuery<
    {
      totalRevenue: number;
      monthlyRevenue: number;
      pendingPayments: number;
      failedPayments: number;
    },
    ApiError
  >({
    queryKey: paymentKeys.stats(),
    queryFn: () => paymentsService.getPaymentStats(),
    staleTime: 60 * 1000,
  });
}

// ============================================================================
// Payment Initiation
// ============================================================================

/**
 * Hook for initiating subscription payment
 * Returns redirect URL to payment gateway checkout
 */
export function useInitiatePayment() {
  const queryClient = useQueryClient();

  return useMutation<
    InitiatePaymentResponse,
    ApiError,
    { orgId: string; paymentMethod?: 'ORANGE_MONEY' | 'MTN_MONEY' | 'CREDIT_CARD' }
  >({
    mutationFn: ({ orgId, paymentMethod }) =>
      paymentsService.initiateSubscriptionPayment(orgId, paymentMethod),
    onSuccess: (response, { orgId }) => {
      if (response.success) {
        // Invalidate related queries
        queryClient.invalidateQueries({ queryKey: paymentKeys.lists() });
        queryClient.invalidateQueries({ queryKey: paymentKeys.orgList(orgId) });
        queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
        queryClient.invalidateQueries({ queryKey: subscriptionKeys.org(orgId) });
      }
    },
  });
}

// ============================================================================
// Payment Status Checking
// ============================================================================

/**
 * Hook to check payment status directly with gateway
 */
export function usePaymentStatus(gateway: string, transactionId: string) {
  return useQuery<PaymentStatusResult, ApiError>({
    queryKey: paymentKeys.status(gateway, transactionId),
    queryFn: () => paymentsService.checkPaymentStatus(gateway, transactionId),
    enabled: !!gateway && !!transactionId,
    staleTime: 10 * 1000, // 10 seconds - status can change frequently
    refetchInterval: 5000, // Poll every 5 seconds when enabled
  });
}

/**
 * Hook for polling payment status and updating database
 */
export function usePollPaymentStatus() {
  const queryClient = useQueryClient();

  return useMutation<PaymentStatusResult, ApiError, string>({
    mutationFn: (transactionId) => paymentsService.pollPaymentStatus(transactionId),
    onSuccess: (result, transactionId) => {
      // Invalidate payment lists when status changes
      if (result.status === 'COMPLETED' || result.status === 'FAILED') {
        queryClient.invalidateQueries({ queryKey: paymentKeys.lists() });
        queryClient.invalidateQueries({ queryKey: paymentKeys.stats() });
        queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      }
    },
  });
}

/**
 * Hook for checking payment status by payment ID.
 * Use this on page load to check if a pending payment has completed.
 */
export function useCheckPaymentStatus() {
  const queryClient = useQueryClient();

  return useMutation<PaymentStatusResult, ApiError, string>({
    mutationFn: (paymentId) => paymentsService.checkPaymentStatusById(paymentId),
    onSuccess: (result) => {
      // Invalidate related queries when status changes
      if (result.status === 'COMPLETED' || result.status === 'FAILED' || result.status === 'CANCELLED') {
        queryClient.invalidateQueries({ queryKey: paymentKeys.lists() });
        queryClient.invalidateQueries({ queryKey: paymentKeys.stats() });
        queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
        queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      }
    },
  });
}

/**
 * Hook to get polling service status (SuperAdmin only)
 */
export function usePollingStatus() {
  return useQuery<PollingStatus, ApiError>({
    queryKey: paymentKeys.pollingStatus(),
    queryFn: () => paymentsService.getPollingStatus(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ============================================================================
// Payment Admin Operations
// ============================================================================

/**
 * Hook for marking payment as paid
 */
export function useMarkPaymentPaid() {
  const queryClient = useQueryClient();

  return useMutation<Payment, ApiError, { id: string; externalReference?: string }>({
    mutationFn: ({ id, externalReference }) =>
      paymentsService.markAsPaid(id, externalReference),
    onSuccess: (updatedPayment, { id }) => {
      queryClient.setQueryData(paymentKeys.detail(id), updatedPayment);
      queryClient.invalidateQueries({ queryKey: paymentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: paymentKeys.stats() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      // Marking a payment paid reactivates the subscription on the backend
      // (clears PAST_DUE / grace). Refresh the summary so the payment-alert
      // banner clears without a manual reload.
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
    },
  });
}

/**
 * Hook for marking payment as failed
 */
export function useMarkPaymentFailed() {
  const queryClient = useQueryClient();

  return useMutation<Payment, ApiError, { id: string; reason?: string }>({
    mutationFn: ({ id, reason }) => paymentsService.markAsFailed(id, reason),
    onSuccess: (updatedPayment, { id }) => {
      queryClient.setQueryData(paymentKeys.detail(id), updatedPayment);
      queryClient.invalidateQueries({ queryKey: paymentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: paymentKeys.stats() });
      // A failed payment can move the subscription to PAST_DUE + grace.
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
    },
  });
}

/**
 * Hook for refunding a payment
 */
export function useRefundPayment() {
  const queryClient = useQueryClient();

  return useMutation<Payment, ApiError, { id: string; reason?: string }>({
    mutationFn: ({ id, reason }) => paymentsService.refundPayment(id, reason),
    onSuccess: (updatedPayment, { id }) => {
      queryClient.setQueryData(paymentKeys.detail(id), updatedPayment);
      queryClient.invalidateQueries({ queryKey: paymentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: paymentKeys.stats() });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
    },
  });
}
